import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ChangeDetectionStrategy, Component, Input, NgZone, OnDestroy, TemplateRef } from "@angular/core";
import { FunctionUtils, LocalComponentStore } from "@dtm-frontend/shared/utils";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Cartesian3 } from "cesium";
import { combineLatestWith, map, tap } from "rxjs";
import { EntityParentCartographicEditPointType } from "../../models/child-entity.model";
import { EditActions, EditModes } from "../../models/edit-actions.enum";
import { EditablePolyline3D, Polyline3DLabel } from "../../models/polyline3d/editable-polyline3d";
import { Polyline3DEditUpdate } from "../../models/polyline3d/polyline3d-edit-update";
import { CameraService } from "../../services/camera.service";
import { CesiumMapService } from "../../services/cesium-map.service";
import { CoordinateConverter } from "../../services/coordinate-converter/coordinate-converter.service";
import { Polylines3DEditorService } from "../../services/entity-editors/polylines3d-editor/polylines3d-editor.service";
import { Polylines3DManagerService } from "../../services/entity-editors/polylines3d-editor/polylines3d-manager.service";
import { HeightHelperService, ShapeDragActions } from "../../services/height-helper.service";
import { CesiumPointerManagerService } from "../../services/pointer-manager/cesium-pointer-manager.service";

interface Polylines3DEditorComponentState {
    isShown: boolean;
    labelTemplate: TemplateRef<any> | null;
    labels: Map<string, Polyline3DLabel[]>;
}

@UntilDestroy()
@Component({
    selector: "dtm-map-polylines3d-editor[show]",
    templateUrl: "./polylines3d-editor.component.html",
    styles: ["dtm-map-html-cesium-component { pointer-events: none}"],
    providers: [Polylines3DManagerService, HeightHelperService, LocalComponentStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Polylines3DEditorComponent implements OnDestroy {
    private editLabelsRenderFunctions = new Map<string, (update: Polyline3DEditUpdate, labels: Polyline3DLabel[]) => Polyline3DLabel[]>();

    protected readonly isShown$ = this.localStore.selectByKey("isShown");
    protected labels$ = this.localStore.selectByKey("labels").pipe(
        combineLatestWith(this.isShown$),
        map(([labels, isShown]) => (isShown ? [...labels.values()].flat().filter((label) => label.show && label.text) : [])),
        // TODO: DTM-5945 after switching to zone-less remove this, it's should work out of hand then
        tap(() => this.ngZone.run(FunctionUtils.noop))
    );
    protected readonly labelTemplate$ = this.localStore.selectByKey("labelTemplate");

    @Input() public set show(value: BooleanInput) {
        const isShown = coerceBooleanProperty(value);
        this.localStore.patchState({ isShown });
        if (isShown) {
            this.polylinesManager.showAll();
        } else {
            this.polylinesManager.hideAll();
        }
    }

    @Input() public set labelTemplate(value: TemplateRef<any> | null) {
        this.localStore.patchState({ labelTemplate: value });
    }

    constructor(
        private readonly polylinesEditor: Polylines3DEditorService,
        private readonly coordinateConverter: CoordinateConverter,
        private readonly cameraService: CameraService,
        private readonly polylinesManager: Polylines3DManagerService,
        private readonly cesiumService: CesiumMapService,
        private readonly cesiumPointerManager: CesiumPointerManagerService,
        private readonly localStore: LocalComponentStore<Polylines3DEditorComponentState>,
        heightHelperService: HeightHelperService,
        private readonly ngZone: NgZone
    ) {
        this.polylinesEditor.init(
            this.coordinateConverter,
            this.cameraService,
            this.polylinesManager,
            this.cesiumService,
            this.cesiumPointerManager,
            heightHelperService
        );
        this.startListeningToEditorUpdates();
        this.localStore.setState({
            isShown: true,
            labelTemplate: null,
            labels: new Map(),
        });
    }

    private startListeningToEditorUpdates() {
        this.polylinesEditor
            .onUpdate()
            .pipe(untilDestroyed(this))
            .subscribe((update: Polyline3DEditUpdate) => {
                if (update.editMode === EditModes.CREATE || update.editMode === EditModes.CREATE_OR_EDIT) {
                    this.handleCreateUpdates(update);
                } else if (update.editMode === EditModes.EDIT) {
                    this.polylinesManager.get(update.id)?.markAsDynamicEntityWhileUpdate();
                    this.handleEditUpdates(update);
                }
            });
    }

    private getLabelId(polyline: EditablePolyline3D, label: Polyline3DLabel): string {
        return `${polyline.getId()}_${label.pointIndex}_${label.id}`;
    }

    public renderEditLabels(polyline: EditablePolyline3D, update: Polyline3DEditUpdate, labels?: Polyline3DLabel[]) {
        update.positions = polyline.getRealPositions(false);
        update.points = polyline.getRealPoints(false);
        update.heights = polyline.getRealHeights(false);
        update.bufferWidths = polyline.getRealBufferWidths(false);
        update.bufferHeights = polyline.getRealBufferHeights(false);
        update.childEntities = polyline.getChildEntities();

        if (labels) {
            polyline.labels = labels;
            this.synchronizeLabels(polyline);

            return;
        }

        const editLabelsRenderFn = this.editLabelsRenderFunctions.get(polyline.getId());

        if (!editLabelsRenderFn) {
            return;
        }

        polyline.labels = editLabelsRenderFn(update, polyline.labels);
        this.synchronizeLabels(polyline);
    }

    private synchronizeLabels(polyline: EditablePolyline3D) {
        this.localStore.patchState(({ labels }) => ({
            labels: new Map(labels).set(
                polyline.getId(),
                polyline.labels.map((label) => ({ ...label, entityId: this.getLabelId(polyline, label) }))
            ),
        }));
    }

    public removeEditLabels(cylinder: EditablePolyline3D) {
        this.localStore.patchState(({ labels }) => {
            const updatedLabels = new Map(labels);
            updatedLabels.delete(cylinder.getId());

            return { labels: updatedLabels };
        });
        cylinder.labels = [];
        this.editLabelsRenderFunctions.delete(cylinder.getId());
    }

    public handleCreateUpdates(update: Polyline3DEditUpdate) {
        switch (update.editAction) {
            case EditActions.INIT: {
                if (update.polylineOptions) {
                    this.polylinesManager.createEditablePolyline3D(update.id, update.polylineOptions);
                }
                break;
            }
            case EditActions.MOUSE_MOVE: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline && update.updatedPosition) {
                    polyline.moveTempMovingPoint(update.updatedPosition as Cartesian3);
                    this.renderEditLabels(polyline, update);
                }
                break;
            }
            case EditActions.ADD_POINT: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline && update.updatedPosition) {
                    polyline.moveTempMovingPoint(update.updatedPosition as Cartesian3);
                    polyline.addPoint(update.updatedPosition as Cartesian3);
                    this.renderEditLabels(polyline, update);
                }
                break;
            }
            case EditActions.DONE: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline) {
                    polyline.finishCreation();
                    this.renderEditLabels(polyline, update);
                }
                break;
            }
            case EditActions.REMOVE_POINT: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline && polyline.enableEdit && update.updatedPoint) {
                    polyline.removePoint(update.updatedPoint);
                    this.renderEditLabels(polyline, update);
                }

                if (polyline && update.shouldRemoveMovingPoint) {
                    polyline.removeTempMovingPoint();
                    this.renderEditLabels(polyline, update);
                }
                break;
            }
            case EditActions.DISPOSE: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline) {
                    polyline.dispose();
                    this.removeEditLabels(polyline);
                }
                break;
            }
            case EditActions.SET_EDIT_LABELS_RENDER_CALLBACK: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline && update.labelsRenderFn) {
                    this.editLabelsRenderFunctions.set(polyline.getId(), update.labelsRenderFn);
                    this.renderEditLabels(polyline, update);
                }
                break;
            }
            case EditActions.UPDATE_EDIT_LABELS: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline) {
                    this.renderEditLabels(polyline, update, update.updateLabels);
                }
                break;
            }
            case EditActions.SET_MANUALLY: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline) {
                    this.renderEditLabels(polyline, update, update.updateLabels);
                }
                break;
            }
            default: {
                return;
            }
        }
    }

    public handleEditUpdates(update: Polyline3DEditUpdate) {
        switch (update.editAction) {
            case EditActions.INIT: {
                if (update.polylineOptions) {
                    this.polylinesManager.createEditablePolyline3D(
                        update.id,
                        update.polylineOptions,
                        update.positions,
                        update.heights,
                        update.bufferWidths,
                        update.bufferHeights
                    );
                }
                break;
            }
            case EditActions.DRAG_POINT: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline && polyline.enableEdit && update.updatedPosition && update.updatedPoint) {
                    if (
                        update.updatedPoint.type === EntityParentCartographicEditPointType.Inlet ||
                        update.updatedPoint.type === EntityParentCartographicEditPointType.Outlet
                    ) {
                        const toPosition = polyline.getStickedPointOnChildEntityOutline(
                            update.updatedPoint,
                            update.updatedPosition as Cartesian3
                        );
                        polyline.movePoint(toPosition, update.updatedPoint);
                    } else {
                        polyline.movePoint(update.updatedPosition as Cartesian3, update.updatedPoint);
                    }

                    this.renderEditLabels(polyline, update);
                }
                break;
            }
            case EditActions.DRAG_POINT_FINISH: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline && polyline.enableEdit && update.updatedPoint) {
                    polyline.movePointFinish(update.updatedPoint);

                    if (update.updatedPoint.isVirtualEditPoint()) {
                        polyline.changeVirtualPointToRealPoint(update.updatedPoint);
                        this.renderEditLabels(polyline, update);
                    }
                }
                break;
            }
            case EditActions.REMOVE_POINT: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline && polyline.enableEdit && update.updatedPoint) {
                    polyline.removePoint(update.updatedPoint);
                    this.renderEditLabels(polyline, update);
                }
                break;
            }
            case EditActions.DISABLE: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline) {
                    polyline.enableEdit = false;
                    this.renderEditLabels(polyline, update);
                }
                break;
            }
            case EditActions.ENABLE: {
                const polyline = this.polylinesManager.get(update.id);
                if (polyline) {
                    polyline.enableEdit = true;
                    this.renderEditLabels(polyline, update);
                }
                break;
            }
            case EditActions.DRAG_SHAPE_FINISH:
            case EditActions.DRAG_SHAPE: {
                const polyline = this.polylinesManager.get(update.id);

                if (!polyline || !polyline.enableEdit || update.shapeDragAction === undefined) {
                    break;
                }

                if (update.editAction === EditActions.DRAG_SHAPE_FINISH && update.shapeDragAction === ShapeDragActions.MoveEntity) {
                    polyline.endMoveShape();

                    break;
                }

                if (polyline && polyline.enableEdit && update.draggedPosition && update.updatedPosition) {
                    polyline.moveShape(update.draggedPosition as Cartesian3, update.updatedPosition);
                    this.renderEditLabels(polyline, update);
                }

                switch (update.shapeDragAction) {
                    case ShapeDragActions.MoveEntity:
                        if (update.draggedPosition && update.updatedPosition) {
                            polyline.moveShape(update.draggedPosition as Cartesian3, update.updatedPosition);
                        }
                        break;

                    case ShapeDragActions.ChangeTopHeight:
                        if (update.height !== undefined && update.updatedHeightPoint !== undefined) {
                            polyline.moveHeight(update);
                        }
                        break;

                    case ShapeDragActions.ChangeBottomHeight:
                        throw new Error("Invalid shapeDragAction");
                }

                this.renderEditLabels(polyline, update);

                break;
            }
            default: {
                return;
            }
        }
    }

    protected trackByEntityId(index: number, label: Polyline3DLabel) {
        return label.entityId;
    }

    public ngOnDestroy(): void {
        this.polylinesManager.clear();
    }
}
